import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Port } from '@app/models/port';
import { PortService } from '@app/services/api/port.service';
import { FlashService } from '@app/services/flash.service';
import {
  AddAll,
  AddOne,
  Create,
  CreateFail,
  CreateSuccess,
  Delete,
  DeleteFail,
  DeleteSuccess,
  Export,
  Load,
  LoadFail,
  LoadSuccess,
  PortActionTypes,
  Update,
  UpdateFail,
  UpdateOne,
  UpdateSuccess,
  UploadProcessSuccess
} from '@core/actions/port.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class PortEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private fs: FlashService,
    private portsApi: PortService
  ) {}

  @Effect()
  uploadProcessSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UploadProcessSuccess>(PortActionTypes.UploadProcessSuccess),
    map(action => new Load())
  );

  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType(PortActionTypes.Load),
    switchMap(() =>
      this.portsApi.get().pipe(
        map(ports => new LoadSuccess(ports)),
        catchError(error => of(new LoadFail(error)))
      )
    )
  );

  @Effect()
  loadSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSuccess>(PortActionTypes.LoadSuccess),
    map(action => new AddAll(action.ports))
  );

  @Effect()
  create$: Observable<Action> = this.actions$.pipe(
    ofType<Create>(PortActionTypes.Create),
    map(action => action.ports),
    switchMap(destination =>
      this.portsApi.create(destination).pipe(
        map((savedPort: Port) => {
          return new CreateSuccess(savedPort);
        }),
        catchError(error => of(new CreateFail(error)))
      )
    )
  );

  @Effect()
  createSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSuccess>(PortActionTypes.CreateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Puerto creado' });
      this.router.navigate(['/ports']);
      return new AddOne(action.ports);
    })
  );

  @Effect()
  delete$: Observable<Action> = this.actions$.pipe(
    ofType<Delete>(PortActionTypes.Delete),
    map(action => action.id),
    concatMap(id =>
      this.portsApi.delete(id).pipe(
        map(destination => new DeleteSuccess(id)),
        catchError(error => of(new DeleteFail(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  deleteSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteSuccess>(PortActionTypes.DeleteSuccess),
    tap(action => {
      this.fs.emit({ type: 'success', message: "Eliminado con éxito" });
    })
  );

  @Effect({ dispatch: false })
  deleteFail$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteFail>(PortActionTypes.DeleteFail),
    tap(action => {
      this.fs.emit({ type: 'danger', message: action["error"].error.message });
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<Update>(PortActionTypes.Update),
    map(action => [action.id, action.port]),
    switchMap(([id, data]) => {
      return this.portsApi.update(id, data).pipe(
        map((ports: Port) => new UpdateSuccess(id, ports)),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateSuccess>(PortActionTypes.UpdateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Puerto actualizado' });
      this.router.navigate(['/ports']);
      return new UpdateOne(action.data);
    })
  );

  @Effect({ dispatch: false })
  proformaDownloabled$ = this.actions$.pipe(
    ofType<Export>(PortActionTypes.Export),
    tap((action) =>
      this.portsApi.downloadPort()
    )
  )
}
